import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";
import { getModule } from "vuex-module-decorators";

import config from "@/config";
import { Role } from "@/global";
import i18n from "@/i18n";
import AccountModule from "@/store/modules/account";
import { setCookie } from "@/utils";

import documentAccessRoutes from "./documentAccess";
import documentRoutes from "./documents";
import registrationRoutes from "./registration";

Vue.use(VueRouter);

const accountState = getModule(AccountModule);

const routes: RouteConfig[] = [
  {
    path: "/",
    redirect: { name: "login" },
  },
  {
    path: "/login",
    name: "login",
    meta: {
      title: "meta.login",
    },
    component: () => import(/* webpackChunkName: "login" */ "../views/Login.vue"),
  },
  {
    path: "/register-patient",
    component: () =>
      import(/* webpackChunkName: "registration" */ "../views/PatientRegistration.vue"),
    children: registrationRoutes,
    beforeEnter: (_to, _from, next) => {
      if (!config.feature.patientRegistration) {
        window.dispatchEvent(new CustomEvent("phellow:event:logout"));
        setCookie("error", "toasts.login.notRegistered", 30);
        next({ name: "login" });
      }

      const { loggedInUserRoles } = accountState;
      if (
        loggedInUserRoles.includes(Role.Patient) ||
        loggedInUserRoles.includes(Role.Representative)
      ) {
        next({ name: "documents" });
      } else {
        next();
      }
    },
  },
  {
    path: "/documents",
    component: () => import(/* webpackChunkName: "documents" */ "../views/Documents.vue"),
    children: documentRoutes,
  },
  {
    name: "vaccinations",
    path: "/vaccinations",
    component: () =>
      import(
        /* webpackChunkName: "vaccinations" */ "../components/vaccinations/VaccinationRecord.vue"
      ),
  },
  {
    path: "/access-rights",
    component: () => import(/* webpackChunkName: "documentAccess" */ "../views/DocumentAccess.vue"),
    children: documentAccessRoutes,
  },
  {
    path: "/access-logs",
    name: "accessLogs",
    meta: {
      title: "meta.accessLogs",
      authorize: [Role.Patient, Role.Representative],
      tile: "accessLogs",
    },
    component: () =>
      import(/* webpackChunkName: "accessLogs" */ "../components/accessLogs/AccessLogs.vue"),
  },
  {
    path: "/patients",
    name: "patients",
    meta: {
      title: "meta.patients",
      authorize: [
        Role.Assistant,
        Role.HealthcareProfessional,
        Role.DocumentAdministrator,
        Role.PolicyAdministrator,
      ],
    },
    component: () => import(/* webpackChunkName: "patients" */ "../views/PatientSearch.vue"),
  },
  {
    path: "/settings",
    name: "userSettings",
    meta: {
      title: "meta.userSettings",
      authorize: [Role.Patient, Role.Representative, Role.PolicyAdministrator],
      tile: "userSettings",
    },
    component: () => import(/* webpackChunkName: "settings" */ "../views/Settings.vue"),
  },
  {
    path: "/profile",
    name: "profile",
    meta: {
      title: "meta.profile",
      authorize: [Role.Patient, Role.Representative],
      tile: "profile",
    },
    component: () => import(/* webpackChunkName: "profile" */ "../views/Profile.vue"),
  },
  {
    path: "/select-role",
    name: "selectRole",
    component: () => import(/* webpackChunkName: "RoleSelection" */ "../views/RoleSelection.vue"),
  },
  {
    path: "*",
    redirect: { name: "login" },
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const scrollBehavior = function (to: any, from: any, savedPosition: any | undefined) {
  // TODO: save document search position and return to it when navigating back from the details page
  document.body.scrollTop = 0;
  return { x: 0, y: 0 };
  /*
  // return desired position
  if (savedPosition) {
    // savedPosition is only available for popstate navigations.
    return savedPosition;
  } else {
    const position: { x?: number; y?: number } = {};
    // check if any matched route config has meta that requires scrolling to top
    if (to.matched.some((m: any) => m.meta.scrollToTop)) {
      // coords will be used if no selector is provided,
      // or if the selector didn't match any element.
      position.x = 0;
      position.y = 0;
    }

    return position;
  }
  */
};

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior,
});

router.beforeEach((to: Route, from: Route, next) => {
  document.title = to.meta?.title || "phellow:web";

  const authorize = to.meta?.authorize as string[] | undefined;
  const hasValidToken = accountState.hasValidToken;
  const currentRole = accountState.role;

  if (authorize) {
    // checks for the presence of the authorize field. Any route that does not contain this
    // at all, does not require authentication.
    if (!hasValidToken) {
      // Not logged in, so redirect to login page
      accountState.setRedirectPath(to.fullPath);
      return next({ name: "login", query: { redirect: to.fullPath } }); // query: { returnUrl: to.path}
    }

    accountState.startSessionPoller();

    // check if route is restricted by role
    if (authorize.length > 0 && !authorize.includes(currentRole)) {
      Vue.notify({
        text: i18n.t("toasts.account.noAppropriatePermission").toString(),
        duration: -1,
      });

      if (from.name) {
        return next(false);
      } else {
        switch (currentRole) {
          case Role.HealthcareProfessional:
          case Role.Assistant:
            return next({ name: "patients" });
          case Role.Patient:
          case Role.Representative:
            return next({ name: "documents" });
        }
      }

      return next("login");
    }
  }

  if (to.name == "documents" && from.name == "vaccination") {
    if (!to.params) {
      to.params = {};
    }

    to.params._reload = "true";
  }

  // Everything is fine, continue
  next();
});

export default router;
